import {
  HeaderContainer,
  HeaderLeft,
  BackButton,
  PageTitle,
  ButtonsContainer,
  StyledButton,
} from "styles/devices/deviceDetailsHeader.styles";
import styled from "styled-components";
import { useAuth } from "context/AuthContext";
import { useDevice, useDeviceStatus } from "hooks/useDevices";
import { Tooltip } from "primereact/tooltip";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { theme } from "styles/theme";

const RemoveAccessButton = styled(StyledButton)`
  && {
    background-color: ${theme.colors.error};
    border-color: ${theme.colors.error};
    color: ${theme.colors.white};
  }

  &&:enabled:hover {
    background-color: ${theme.colors.statusInactiveText};
    border-color: ${theme.colors.statusInactiveText};
    color: ${theme.colors.white};
  }
`;

type UpdateType = "firmware" | "nipper" | null;

interface DeviceHeaderProps {
  serialNumber: string;
  onUpdateClick: (type: UpdateType) => void;
  onDeleteClick: () => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

function DeviceHeader({
  serialNumber,
  onUpdateClick,
  onDeleteClick,
  isUpdating,
  isDeleting,
}: DeviceHeaderProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "ADMIN";
  const backPath = isAdmin ? "/all-devices" : "/devices";

  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
      return;
    }

    navigate(backPath);
  };

  const { data: deviceStatus } = useDeviceStatus(serialNumber);
  const [searchParams] = useSearchParams();
  const linkIdParam = searchParams.get("linkId");
  const linkId =
    linkIdParam && linkIdParam !== "undefined" && linkIdParam !== "null"
      ? linkIdParam
      : undefined;
  const { data: deviceData } = useDevice(serialNumber, linkId);

  const shadow = deviceStatus?.response?.shadow;
  const noFlow = shadow?.flow === 0 || shadow?.nipperConnection === 0;
  const isEu = deviceData?.region === 1;
  const nipperName = isEu ? "EcoSalt2" : "Nipper";

  return (
    <HeaderContainer>
      <HeaderLeft>
        <BackButton type="button" onClick={handleBack}>
          &larr; Back to Devices
        </BackButton>
        <PageTitle>Device Details</PageTitle>
      </HeaderLeft>
      <ButtonsContainer>
        <StyledButton
          type="button"
          label="Update Lifeguard"
          icon="pi pi-cloud-download"
          onClick={() => onUpdateClick("firmware")}
          disabled={isUpdating}
        />
        <div id="nipper-update-button">
          <StyledButton
            type="button"
            label={`Update ${nipperName}`}
            icon="pi pi-server"
            onClick={() => onUpdateClick("nipper")}
            disabled={isUpdating || noFlow}
          />
        </div>
        {(isUpdating || noFlow) && (
          <Tooltip
            target="#nipper-update-button"
            content={
              isUpdating
                ? "Update in progress..."
                : `Ensure ${nipperName} is powered on and the pump is running before starting the update.`
            }
            position="top"
          />
        )}
        {!isAdmin && linkId && (
          <RemoveAccessButton
            type="button"
            label="Remove Access"
            icon="pi pi-unlink"
            onClick={onDeleteClick}
            disabled={isDeleting}
          />
        )}
      </ButtonsContainer>
    </HeaderContainer>
  );
}

export default DeviceHeader;
